import React, { useState } from 'react';
import { Calculator, Copy, Trash2, Tag } from 'lucide-react';

const DiscountCalculatorTool = () => {
  const [originalPrice, setOriginalPrice] = useState('');
  const [discountPercent, setDiscountPercent] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const calculate = () => {
    const price = parseFloat(originalPrice);
    const discount = parseFloat(discountPercent);

    if (isNaN(price) || isNaN(discount) || price <= 0) {
      setResult(null);
      return;
    }

    // Savings = Original Price × (Discount% / 100)
    const savings = price * (discount / 100);
    const finalPrice = price - savings;

    setResult({
      originalPrice: price,
      discountPercent: discount,
      savings,
      finalPrice
    });
  };

  const copyResult = (value) => {
    navigator.clipboard.writeText(value.toFixed(2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const clearAll = () => {
    setOriginalPrice('');
    setDiscountPercent('');
    setResult(null);
  };

  const formatCurrency = (value) => {
    return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Original Price ($)
            </label>
            <input
              type="number"
              step="0.01"
              value={originalPrice}
              onChange={(e) => setOriginalPrice(e.target.value)}
              placeholder="e.g., 100"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="original-price-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Discount (%)
            </label>
            <input
              type="number"
              step="0.1"
              value={discountPercent}
              onChange={(e) => setDiscountPercent(e.target.value)}
              placeholder="e.g., 25"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="discount-percent-input"
            />
          </div>
        </div>
      </div>

      {/* Calculate Button */}
      <button
        onClick={calculate}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        data-testid="calculate-discount-btn"
      >
        <Calculator size={18} />
        Calculate Discount
      </button>

      {/* Results Section */}
      {result && (
        <div className="p-4 bg-gray-800 rounded-lg space-y-4" data-testid="discount-results">
          {/* Main Results */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Final Price</span>
                <button
                  onClick={() => copyResult(result.finalPrice)}
                  className={`p-1.5 rounded transition-colors ${
                    copied ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  data-testid="copy-final-price"
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-3xl font-bold text-green-400" data-testid="final-price-result">
                {formatCurrency(result.finalPrice)}
              </div>
            </div>

            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">You Save</span>
                <button
                  onClick={() => copyResult(result.savings)}
                  className={`p-1.5 rounded transition-colors ${
                    copied ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  data-testid="copy-savings"
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-3xl font-bold text-yellow-400" data-testid="savings-result">
                {formatCurrency(result.savings)}
              </div>
            </div>
          </div>

          {/* Visual Breakdown */}
          <div className="p-4 bg-gray-900 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <Tag size={16} className="text-green-400" />
              <span className="text-sm font-medium text-gray-300">Price Breakdown</span>
            </div>
            <div className="relative h-8 bg-gray-700 rounded-lg overflow-hidden">
              <div 
                className="absolute left-0 top-0 h-full bg-green-500 rounded-l-lg"
                style={{ width: `${(result.finalPrice / result.originalPrice) * 100}%` }}
              />
              <div 
                className="absolute right-0 top-0 h-full bg-yellow-500 rounded-r-lg"
                style={{ width: `${(result.savings / result.originalPrice) * 100}%` }}
              />
            </div>
            <div className="flex justify-between mt-2 text-xs text-gray-400">
              <span>Final Price ({((result.finalPrice / result.originalPrice) * 100).toFixed(0)}%)</span>
              <span>Savings ({result.discountPercent}%)</span>
            </div>
          </div>

          {/* Summary */}
          <div className="border-t border-gray-700 pt-4">
            <h4 className="text-sm font-medium text-gray-300 mb-3">Calculation Summary</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Original Price</span>
                <span className="text-white font-medium">{formatCurrency(result.originalPrice)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Discount</span>
                <span className="text-white font-medium">{result.discountPercent}%</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Savings</span>
                <span className="text-yellow-400 font-medium">-{formatCurrency(result.savings)}</span>
              </div>
              <div className="flex justify-between text-sm border-t border-gray-700 pt-2 mt-2">
                <span className="text-gray-400">Final Price</span>
                <span className="text-green-400 font-medium">{formatCurrency(result.finalPrice)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Formula Section */}
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Discount Formula</h3>
        <div className="text-sm text-gray-400 font-mono bg-gray-900 p-3 rounded space-y-1">
          <div>Savings = Original Price × (Discount% / 100)</div>
          <div>Final Price = Original Price - Savings</div>
        </div>
      </div>

      {/* Info Section */}
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Common Discount Amounts</h3>
        <div className="grid grid-cols-4 gap-4 text-sm text-gray-400">
          <div className="text-center">
            <div className="text-white font-medium">10%</div>
            <div className="text-xs">Small sale</div>
          </div>
          <div className="text-center">
            <div className="text-white font-medium">25%</div>
            <div className="text-xs">Good deal</div>
          </div>
          <div className="text-center">
            <div className="text-white font-medium">50%</div>
            <div className="text-xs">Half off</div>
          </div>
          <div className="text-center">
            <div className="text-white font-medium">75%</div>
            <div className="text-xs">Clearance</div>
          </div>
        </div>
      </div>

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Shopping Tips</h3>
        <div className="text-sm text-gray-400 space-y-1">
          <div>• Compare final prices, not just discount percentages</div>
          <div>• Check if the "original price" is inflated</div>
          <div>• Consider price per unit for better comparison</div>
        </div>
      </div>

      {/* Clear Button */}
      <button
        onClick={clearAll}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        data-testid="clear-discount-btn"
      >
        <Trash2 size={16} />
        Clear All
      </button>
    </div>
  );
};

export default DiscountCalculatorTool;
